import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  ApiError,
  Button,
  apiFetch,
  cn,
  type PortalChatRequest,
  type PortalChatResponse,
} from "@dearbill/shared";
import { parseAgentReply, toMessageBlocks } from "./chatFormat";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

interface ChatMessage {
  id: number;
  from: "member" | "agent";
  text: string;
  /** Set on a member message whose send failed; the row offers a retry. */
  failed?: boolean;
  /** The failed send hit the chat rate limit (429), which gets its own wait message. */
  rateLimited?: boolean;
  /** Tap-to-answer choices stripped from an agent reply's options line. */
  quickReplies?: string[];
}

const GREETING =
  "Hi! I can answer questions about your programs and eligibility. What can I help you with?";

interface ChatWidgetProps {
  /** Portal session bearer; the widget renders only for a signed-in member. */
  session: string;
}

/**
 * The member chat: a floating button that slides up a mobile-first bottom
 * sheet (full-width on phones, a corner panel from `sm:`). Each send is
 * optimistic: the member's bubble appears immediately, a typing indicator
 * shows while `POST /api/portal/chat` is in flight, and a failed send marks
 * the bubble with a Retry affordance instead of losing the text.
 * `escalated: true` pins the specialist-follow-up banner for the rest of the
 * session. The `sessionId` from each reply continues the thread. The server
 * binds that id to the signed-in member, so it is dropped when the portal
 * session changes and when a send is refused with 403; the next send then
 * starts a fresh thread. A 429 tells the member to slow down.
 */
export function ChatWidget({ session }: ChatWidgetProps): ReactNode {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { id: 0, from: "agent", text: GREETING },
  ]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const chatSessionId = useRef<string | null>(null);
  const nextId = useRef(1);
  const logRef = useRef<HTMLDivElement | null>(null);

  // A chat thread belongs to one member session; a new bearer starts fresh.
  useEffect(() => {
    chatSessionId.current = null;
  }, [session]);

  // Keep the newest message visible as the log grows or the panel opens.
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [messages, sending, open]);

  const send = (text: string, retryId?: number): void => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    const id = retryId ?? nextId.current++;
    setMessages((prev) => {
      const others = prev.filter((m) => m.id !== id);
      return [...others, { id, from: "member", text: trimmed }];
    });
    setSending(true);
    apiFetch<PortalChatResponse>(
      "/api/portal/chat",
      {
        method: "POST",
        body: {
          message: trimmed,
          sessionId: chatSessionId.current,
        } satisfies PortalChatRequest,
      },
      { baseUrl: API_BASE, token: session },
    )
      .then((res) => {
        chatSessionId.current = res.sessionId;
        if (res.escalated) setEscalated(true);
        const { text: replyText, quickReplies } = parseAgentReply(res.reply);
        setMessages((prev) => [
          ...prev,
          { id: nextId.current++, from: "agent", text: replyText, quickReplies },
        ]);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 403) chatSessionId.current = null;
        const rateLimited = err instanceof ApiError && err.status === 429;
        setMessages((prev) =>
          prev.map((m) => (m.id === id ? { ...m, failed: true, rateLimited } : m)),
        );
      })
      .finally(() => setSending(false));
  };

  // Tapping a pill answers for the member and retires that bubble's pills,
  // so a stale closed choice can't be re-sent later in the thread.
  const sendQuickReply = (messageId: number, option: string): void => {
    if (sending) return;
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, quickReplies: [] } : m)));
    send(option);
  };

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!draft.trim()) return;
    send(draft);
    setDraft("");
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Chat with us"
        className="bg-ember fixed bottom-5 right-5 z-40 flex size-16 cursor-pointer items-center justify-center rounded-full text-white shadow-lg transition hover:opacity-90 active:scale-95"
      >
        <ChatIcon />
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Chat with DearBill"
      className="border-haze fixed inset-x-0 bottom-0 z-40 flex max-h-[85dvh] flex-col rounded-t-3xl border bg-white shadow-2xl sm:inset-x-auto sm:bottom-5 sm:right-5 sm:h-[600px] sm:w-96 sm:rounded-3xl"
    >
      <div className="bg-deep-navy flex items-center justify-between rounded-t-3xl px-5 py-4">
        <p className="text-body font-bold text-white">Chat with DearBill</p>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close chat"
          className="flex size-10 cursor-pointer items-center justify-center rounded-full text-white hover:bg-white/10"
        >
          <span aria-hidden="true" className="text-xl leading-none">
            ×
          </span>
        </button>
      </div>

      {escalated && (
        <p role="status" className="bg-lemon text-ochre px-5 py-3 text-[15px] font-medium">
          A specialist will follow up with you.
        </p>
      )}

      <div ref={logRef} className="flex min-h-40 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
        {messages.map((m) => (
          <div
            key={m.id}
            className={cn("flex flex-col gap-1", m.from === "member" ? "items-end" : "items-start")}
          >
            <div
              className={cn(
                "max-w-[85%] rounded-2xl px-4 py-2.5 text-[16px] leading-snug",
                m.from === "member" ? "bg-deep-navy text-white" : "bg-ice text-midnight",
              )}
            >
              <MessageBody text={m.text} />
            </div>
            {m.from === "agent" && m.quickReplies && m.quickReplies.length > 0 && (
              <div className="flex max-w-[85%] flex-wrap gap-2 pt-1">
                {m.quickReplies.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-label={`Reply: ${option}`}
                    onClick={() => sendQuickReply(m.id, option)}
                    disabled={sending}
                    className="border-ember text-primary-text hover:bg-soft-hover active:bg-soft-active min-h-11 cursor-pointer rounded-full border-2 bg-white px-4 text-[16px] font-semibold disabled:cursor-default disabled:opacity-50"
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
            {m.failed && (
              <p className="text-danger text-[13px]">
                {m.rateLimited
                  ? "You’re sending messages quickly — please wait a moment."
                  : "Not sent."}{" "}
                <button
                  type="button"
                  onClick={() => send(m.text, m.id)}
                  className="cursor-pointer font-semibold underline hover:no-underline"
                >
                  Retry
                </button>
              </p>
            )}
          </div>
        ))}
        {sending && (
          <p
            aria-label="The assistant is typing"
            className="bg-ice text-slate-teal max-w-[85%] animate-pulse self-start rounded-2xl px-4 py-2.5 text-[16px]"
          >
            …
          </p>
        )}
      </div>

      <form onSubmit={onSubmit} className="border-haze flex items-end gap-2 border-t px-4 py-3">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="Your message"
          placeholder="Type your question…"
          autoComplete="off"
          className="border-haze bg-input-bg text-midnight placeholder:text-placeholder h-12 min-w-0 flex-1 rounded-2xl border-2 px-4 text-[16px] outline-none focus:border-slate-400"
        />
        <Button type="submit" size="md" disabled={!draft.trim() || sending}>
          Send
        </Button>
      </form>
    </div>
  );
}

/**
 * A bubble's text as paragraph and dash-list blocks (see `toMessageBlocks`).
 * The agent's chat contract is plain text, so there is no markdown pass; any
 * unrecognized line renders verbatim as a paragraph.
 */
function MessageBody({ text }: { text: string }): ReactNode {
  return (
    <div className="flex flex-col gap-1.5">
      {toMessageBlocks(text).map((block, i) =>
        block.kind === "list" ? (
          <ul key={i} className="list-disc space-y-1 pl-5">
            {block.items.map((item, j) => (
              <li key={j}>{item}</li>
            ))}
          </ul>
        ) : (
          <p key={i}>{block.text}</p>
        ),
      )}
    </div>
  );
}

function ChatIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="size-7">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
